import { useState } from 'react';
import { useAsset, ASSET_CATEGORY_LABELS, ASSET_STATUS_LABELS, ASSET_STATUS_COLORS, useUpdateAsset, useDeleteAsset, AssetStatus } from '@/hooks/useAssets';
import { usePermissions } from '@/hooks/usePermissions';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { X, Trash2, MapPin, Package, Calendar, FileText, Clock, Camera, File, PowerOff, Settings } from 'lucide-react';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { AssetOverviewTab } from './AssetOverviewTab';
import { AssetMaintenanceTab } from './AssetMaintenanceTab';
import { AssetUsageTab } from './AssetUsageTab';
import { AssetPhotosTab } from './AssetPhotosTab';
import { AssetDocumentsTab } from './AssetDocumentsTab';
import { AssetDowntimeTab } from './AssetDowntimeTab';
import { JobRulesManager } from './JobRulesManager';
import { useActiveAssetDowntime } from '@/hooks/useAssetDowntime';
import { isAssetAvailable } from '@/lib/optiassets/isAssetAvailable';

interface AssetDetailProps {
  assetId: string;
  onClose: () => void;
}

export function AssetDetail({ assetId, onClose }: AssetDetailProps) {
  const { data: asset, isLoading } = useAsset(assetId);
  const { isManager, canEditAssets, canDeleteAssets } = usePermissions();
  const { isServiceWriter } = usePermissions();
  const canManageJobRules = isManager || isServiceWriter;
  const updateAsset = useUpdateAsset();
  const deleteAsset = useDeleteAsset();
  const { active: activeDowntime } = useActiveAssetDowntime(assetId);
  const [statusReason, setStatusReason] = useState('');
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<AssetStatus | null>(null);

  const handleStatusChange = (newStatus: AssetStatus) => {
    if (newStatus === asset?.status) return;
    
    // For non-active statuses, prompt for reason
    if (newStatus !== 'active') {
      setPendingStatus(newStatus);
      setShowStatusDialog(true);
    } else {
      updateAsset.mutate({ id: assetId, status: newStatus, status_reason: null });
    }
  };

  const confirmStatusChange = () => {
    if (!pendingStatus) return;
    updateAsset.mutate({ 
      id: assetId, 
      status: pendingStatus, 
      status_reason: statusReason || null 
    });
    setShowStatusDialog(false);
    setPendingStatus(null);
    setStatusReason('');
  };

  const handleDelete = async () => {
    await deleteAsset.mutateAsync(assetId);
    onClose();
  };

  if (isLoading) {
    return (
      <div className="flex flex-col h-full">
        <div className="p-4 border-b flex items-start gap-4">
          <Skeleton className="h-12 w-12 rounded-lg" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-32" />
          </div>
        </div>
        <div className="flex-1 p-4">
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (!asset) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8">
        <Package className="h-12 w-12 text-muted-foreground mb-4" />
        <p className="text-muted-foreground">Asset not found</p>
        <Button variant="outline" className="mt-4" onClick={onClose}>Close</Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b">
        {/* Downtime banner */}
        {activeDowntime && (
          <div className="mb-3 px-3 py-2 rounded-md border border-destructive/40 bg-destructive/5 flex items-center gap-2 text-sm">
            <PowerOff className="h-4 w-4 text-destructive flex-shrink-0" />
            <span className="text-destructive font-medium">Out of service</span>
            <span className="text-muted-foreground truncate">— {activeDowntime.reason}</span>
          </div>
        )}
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className={cn(
              'w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center',
              !isAssetAvailable(asset, activeDowntime) && 'ring-2 ring-destructive/50'
            )}>
              <Package className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold">{asset.name}</h2>
              <p className="text-sm text-muted-foreground">
                {ASSET_CATEGORY_LABELS[asset.category]}
                {asset.manufacturer && ` • ${asset.manufacturer}`}
                {asset.model && ` ${asset.model}`}
              </p>
              {asset.home_bay && (
                <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                  <MapPin className="h-3 w-3" />
                  {asset.home_bay.name}
                </div>
              )}
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Status Control */}
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          {canEditAssets ? (
            <Select value={asset.status} onValueChange={(v) => handleStatusChange(v as AssetStatus)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(ASSET_STATUS_LABELS).map(([key, label]) => (
                  <SelectItem key={key} value={key}>
                    <div className="flex items-center gap-2">
                      <div className={cn('w-2 h-2 rounded-full', 
                        key === 'active' ? 'bg-green-500' :
                        key === 'out_of_service' ? 'bg-red-500' :
                        key === 'maintenance' ? 'bg-yellow-500' :
                        key === 'awaiting_parts' ? 'bg-orange-500' : 'bg-gray-500'
                      )} />
                      {label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Badge className={ASSET_STATUS_COLORS[asset.status]}>
              {ASSET_STATUS_LABELS[asset.status]}
            </Badge>
          )}
          
          {asset.status_reason && (
            <span className="text-sm text-muted-foreground italic">
              "{asset.status_reason}"
            </span>
          )}

          {canDeleteAssets && (
            <div className="ml-auto">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive">
                    <Trash2 className="h-4 w-4 mr-1" />
                    Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Asset</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will permanently delete "{asset.name}" and all associated records. This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction 
                      onClick={handleDelete}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
        </div>
      </div>

      {/* Status Reason Dialog */}
      <AlertDialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Update Status</AlertDialogTitle>
            <AlertDialogDescription>
              Optionally provide a reason for this status change.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            placeholder="e.g., Waiting for parts from vendor..."
            value={statusReason}
            onChange={(e) => setStatusReason(e.target.value)}
          />
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setPendingStatus(null); setStatusReason(''); }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmStatusChange}>
              Update Status
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="flex-1 flex flex-col overflow-hidden">
        <TabsList className="mx-4 mt-4 justify-start flex-wrap h-auto gap-1">
          <TabsTrigger value="overview" className="gap-2">
            <FileText className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="photos" className="gap-2">
            <Camera className="h-4 w-4" />
            Photos
          </TabsTrigger>
          <TabsTrigger value="documents" className="gap-2">
            <File className="h-4 w-4" />
            Docs
          </TabsTrigger>
          <TabsTrigger value="maintenance" className="gap-2">
            <Calendar className="h-4 w-4" />
            Maintenance
          </TabsTrigger>
          <TabsTrigger value="usage" className="gap-2">
            <Clock className="h-4 w-4" />
            Usage
          </TabsTrigger>
          <TabsTrigger value="downtime" className="gap-2">
            <PowerOff className="h-4 w-4" />
            Downtime
            {activeDowntime && (
              <span className="ml-1 inline-block w-2 h-2 rounded-full bg-destructive" />
            )}
          </TabsTrigger>
          {canManageJobRules && (
            <TabsTrigger value="rules" className="gap-2">
              <Settings className="h-4 w-4" />
              Job Rules
            </TabsTrigger>
          )}
        </TabsList>

        <div className="flex-1 overflow-y-auto">
          <TabsContent value="overview" className="m-0 p-4">
            <AssetOverviewTab asset={asset} />
          </TabsContent>
          <TabsContent value="photos" className="m-0 p-4">
            <AssetPhotosTab assetId={assetId} />
          </TabsContent>
          <TabsContent value="documents" className="m-0 p-4">
            <AssetDocumentsTab assetId={assetId} />
          </TabsContent>
          <TabsContent value="maintenance" className="m-0 p-4">
            <AssetMaintenanceTab assetId={assetId} />
          </TabsContent>
          <TabsContent value="usage" className="m-0 p-4">
            <AssetUsageTab assetId={assetId} />
          </TabsContent>
          <TabsContent value="downtime" className="m-0 p-4">
            <AssetDowntimeTab assetId={assetId} />
          </TabsContent>
          {canManageJobRules && (
            <TabsContent value="rules" className="m-0 p-4">
              <JobRulesManager assetCategoryFilter={asset.category} />
            </TabsContent>
          )}
        </div>
      </Tabs>
    </div>
  );
}
